import {
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  useState,
  ReactNode,
  createElement,
} from 'react';

import { TableColumnSortDirection } from '../../components/Table/shared';
import { useComparator } from '../../locale/sorting/useComparator';
import {
  ColumnKeys,
  ContentComparator,
  getSortComparator,
  getSortCriteria,
} from '../util/sortUtil';

export type ContentSortDispatchValue = {
  direction: TableColumnSortDirection | undefined;
  key: ColumnKeys;
};

export type ContentSortContextValue = ContentSortDispatchValue & {
  sortByComparator?: ContentComparator;
  sortByCriteria?: string;
};

// create a context and provider for content sort
const ContentSortContext = createContext<ContentSortContextValue | undefined>(undefined);

const ContentSortDispatch = createContext<
  Dispatch<SetStateAction<ContentSortDispatchValue | undefined>> | undefined
>(undefined);

// create a hook function to set the content sort
export const useContentSort = () => {
  return useContext(ContentSortContext);
};

export const useContentSortDispatch = () => {
  return useContext(ContentSortDispatch);
};

export interface ContentSortProviderProps {
  children?: ReactNode;
  initialState?: ContentSortDispatchValue;
}

/**
 * Provides a shared item sort context for components that consume this provider. Components can
 * consume and dispatch changes to the content sort state using the hooks.
 * @param initialState
 * @param children
 * @constructor
 */
export const ContentSortProvider = ({
  initialState = undefined,
  children,
}: ContentSortProviderProps) => {
  const [contentSort, setContentSort] = useState<ContentSortDispatchValue | undefined>(
    initialState
  );

  const sortDir =
    (contentSort?.direction !== 'data' ? contentSort?.direction : 'ascending') ?? 'ascending';
  const sortKey = contentSort?.key ?? 'name';

  const stringComparator = useComparator<string>({
    toSortKey: (s) => s,
    direction: sortDir,
    sensitivity: 'strict',
  });

  // externalize stringComparator so getSortComparator can be tested without hooks
  const sortComparator = getSortComparator(sortKey, sortDir, stringComparator);
  const sortCriteria = getSortCriteria(sortKey, sortDir);

  return (
    <ContentSortContext.Provider
      value={{
        key: sortKey,
        direction: sortDir,
        sortByComparator: sortComparator,
        sortByCriteria: sortCriteria,
      }}
    >
      <ContentSortDispatch.Provider value={setContentSort}>{children}</ContentSortDispatch.Provider>
    </ContentSortContext.Provider>
  );
};
